/** Shared advanced-filter state + chip helpers for the public firm listing. */

export const emptyAdvancedFilters = () => ({
  countries: [],
  platforms: [],
  markets: [],
  programs: [],
  yearsMin: null,
  yearsMax: null,
  rankMin: null,
  rankMax: null,
  accountSizeMin: null,
  accountSizeMax: null,
});

export function countActiveAdvancedFilters(filters) {
  if (!filters) return 0;
  let n = 0;
  n += filters.countries?.length ?? 0;
  n += filters.platforms?.length ?? 0;
  n += filters.markets?.length ?? 0;
  n += filters.programs?.length ?? 0;
  if (filters.yearsMin != null || filters.yearsMax != null) n += 1;
  if (filters.rankMin != null || filters.rankMax != null) n += 1;
  if (filters.accountSizeMin != null || filters.accountSizeMax != null) n += 1;
  return n;
}

/** Format dollars for range labels: 1000 → $1K, 500000 → $500K */
export function formatAccountDollars(value) {
  if (value == null || !Number.isFinite(Number(value))) return "—";
  const n = Number(value);
  if (n >= 1000) {
    const k = n / 1000;
    return `$${Number.isInteger(k) ? k : k.toFixed(1)}K`;
  }
  return `$${n}`;
}

/** Account size slider step: $10,000 */
export const ACCOUNT_SIZE_STEP = 10_000;

/** Snap a value to the nearest step within [minBound, maxBound]. */
export function snapToStep(value, step, minBound, maxBound) {
  if (value == null || !Number.isFinite(Number(value))) return null;
  const snapped = Math.round(Number(value) / step) * step;
  return Math.min(Math.max(snapped, minBound), maxBound);
}

/** Snap API account-size bounds to 10K steps for the slider track. */
export function snapAccountSizeBounds(apiMin, apiMax, step = ACCOUNT_SIZE_STEP) {
  const rawMin = Number(apiMin);
  const rawMax = Number(apiMax);
  const minBound = Number.isFinite(rawMin)
    ? Math.floor(rawMin / step) * step
    : 0;
  const maxBound = Number.isFinite(rawMax)
    ? Math.ceil(rawMax / step) * step
    : step;
  return {
    minBound: Math.min(minBound, maxBound),
    maxBound: Math.max(minBound, maxBound),
  };
}

/**
 * Build chip labels from current filters + options from GET /prop-firms/filters.
 */
export function getActiveFilterChips(filters, options = {}) {
  const chips = [];
  if (!filters) return chips;

  const pushMulti = (group, selected, opts, idKey, labelKey) => {
    (selected || []).forEach((id) => {
      const opt = (opts || []).find((o) => o[idKey] === id);
      chips.push({
        key: `${group}:${id}`,
        group,
        id,
        label: opt?.[labelKey] ?? id,
      });
    });
  };

  pushMulti("countries", filters.countries, options.countries, "code", "name");
  pushMulti("platforms", filters.platforms, options.platforms, "slug", "name");
  pushMulti("markets", filters.markets, options.markets, "slug", "name");
  pushMulti("programs", filters.programs, options.programs, "value", "label");

  if (filters.yearsMin != null || filters.yearsMax != null) {
    const min = filters.yearsMin ?? options.years?.min ?? 0;
    const max = filters.yearsMax ?? options.years?.max ?? min;
    chips.push({
      key: "years",
      group: "years",
      label: `${min}–${max} yrs`,
    });
  }

  if (filters.rankMin != null || filters.rankMax != null) {
    const min = filters.rankMin ?? options.rank?.min ?? 1;
    const max = filters.rankMax ?? options.rank?.max ?? min;
    chips.push({
      key: "rank",
      group: "rank",
      label: `#${min}–#${max}`,
    });
  }

  if (filters.accountSizeMin != null || filters.accountSizeMax != null) {
    const min = filters.accountSizeMin ?? options.accountSize?.min ?? 0;
    const max = filters.accountSizeMax ?? options.accountSize?.max ?? min;
    chips.push({
      key: "accountSize",
      group: "accountSize",
      label: `${formatAccountDollars(min)}–${formatAccountDollars(max)}`,
    });
  }

  return chips;
}

/** Append advanced filter params to a URLSearchParams instance. */
export function appendFirmFilterParams(params, filters) {
  if (!filters) return;

  const csv = (key, arr) => {
    if (arr?.length) params.set(key, arr.join(","));
  };

  csv("countries", filters.countries);
  csv("platforms", filters.platforms);
  csv("markets", filters.markets);
  csv("programs", filters.programs);

  const setNum = (key, val) => {
    if (val != null && Number.isFinite(Number(val))) {
      params.set(key, String(val));
    }
  };

  setNum("yearsMin", filters.yearsMin);
  setNum("yearsMax", filters.yearsMax);
  setNum("rankMin", filters.rankMin);
  setNum("rankMax", filters.rankMax);
  setNum("accountSizeMin", filters.accountSizeMin);
  setNum("accountSizeMax", filters.accountSizeMax);
}
